import { apiClient } from "./client";

/**
 * A single BoQ resource entry on a task — pairs a resource code with the
 * per-plot resource quantity. `qty × itemLibraryRate(code)` matches this
 * plot's cost contribution for the resource.
 *
 *   • `qty`        — effective per-plot quantity (= `actualQty × multiplier`).
 *                    Always present.
 *   • `actualQty`  — raw per-plot take-off quantity before any rate-side
 *                    multiplier (Plot Matrix cell input). Mirrors `qty` when
 *                    no multiplier applies.
 *   • `multiplier` — conversion factor baked into the BoQ rate. Examples:
 *                      – Drainage per-plot bend rows: `labQty / nrPlotsTakeOff`
 *                        (per-plot labour conversion).
 *                      – Plot Service apartments rows: the apartments multiple.
 *                      – Retaining-walls build-up children: `item.net × N`.
 *                      – M³→m² thickness rows: the thickness in metres.
 *                      – Ordinary rows: omitted (identity multiplier).
 */
export interface ResourceEntry {
  code: string;
  qty: number;
  actualQty?: number;
  multiplier?: number;
}

export interface Task {
  taskName: string;
  /**
   * BoQ resource entries for every field aggregated under this task on
   * this plot. Each entry pairs a code with the activity quantity read
   * from the plot's row of the corresponding step's take-off table.
   * Codes are deduplicated by summing qtys (so a category whose fields
   * share a code lists it once); empty / `[dynamic]` codes and
   * zero-qty entries are filtered out. Populated by
   * `resolveFieldResources` in `plotMatrixFormulaDefinitions.tsx`.
   * Example: `[{ code: "GWALP001", qty: 3 }, { code: "GWALP002", qty: 4 }]`.
   * May be `[]` for a task whose fields all carry no direct
   * item-library code or no quantity on this plot.
   */
  resource: ResourceEntry[];
}

/**
 * Stage structure
 */
export interface Stage {
  stageName: string;
  stageCode: string;
  total: number;
  tasks: Task[];
}

/**
 * Plot structure for groundworks
 */
export interface Plot {
  plotNo: string;
  houseType: string;
  subTotal: number;
  discount: number;
  total: number;
  stages: Stage[];
}

/**
 * Run structure for roads and sewers
 */
export interface Run {
  runNo: string;
  total: number;
  stages: Stage[];
}

/**
 * Global settings for a job type section
 */
export interface SectionGlobalSettings {
  overheadAndProfit: number;
  preliminaries: number;
  discount: number;
}

/**
 * Groundworks section
 */
export interface Groundworks {
  globalSettings?: SectionGlobalSettings;
  plots: Plot[];
}

/**
 * Roads and sewers section
 */
export interface RoadsAndSewers {
  runs: Run[];
}

/**
 * Request payload for creating a plot submission
 */
export interface CreatePlotSubmissionRequest {
  projectId: string;
  projectName: string;
  opportunityNo?: string;
  client?: string;
  region?: string;
  siteAddress?: string;
  submittedBy: string;
  submittedAt: string;
  groundworks?: Groundworks;
  roadsAndSewers?: RoadsAndSewers;
}

/**
 * Response from plot submission API
 */
export interface PlotSubmissionResponse {
  success: boolean;
  message?: string;
  data?: {
    submissionId?: string;
    projectId?: string;
    projectName?: string;
    submittedAt?: string;
  };
}

/**
 * Plot Submission API - handles plot submission to central projects register
 */
export const plotSubmissionApi = {
  /**
   * Create a new plot submission
   * @param data - The plot submission data
   * @returns Promise with the submission response
   */
  create: async (
    data: CreatePlotSubmissionRequest
  ): Promise<PlotSubmissionResponse> => {
    try {
      const response = await apiClient.post<PlotSubmissionResponse>(
        "/v3/central/projects/register",
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error creating plot submission:", error);
      throw error;
    }
  },
};
